import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import ConciergeInboxTemplateViewDialog, {
  useConciergeInboxTemplateViewDialogState,
} from "../components/ConciergeInboxTemplateViewDialog"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"

const APPROVALS_URL = "/crm/api/concierge/inbox-template-approvals/"

function mediumLabel(medium) {
  return medium === "sms" ? "SMS" : "Email"
}

function TemplateApprovalRow({
  template,
  actingId,
  onReject,
  onInactive,
  onReactivate,
  onView,
}) {
  const busy = actingId === `${template.medium}-${template.id}`
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Row
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Column sx={{ minWidth: 0, gap: 0.75, flex: 1 }}>
          <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography fontWeight={700}>{template.name}</Typography>
            <Chip size="small" label={mediumLabel(template.medium)} />
            <InboxTemplateStatusChip status={template.approval_status} />
          </Row>
          <Typography variant="body2" color="text.secondary" noWrap>
            {template.preview || "No preview"}
          </Typography>
        </Column>
        <Row sx={{ gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => onView(template)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            View
          </Button>
          {template.approval_status === "approved" ? (
            <>
              <LoadingButton
                size="small"
                variant="outlined"
                color="warning"
                loading={busy}
                onClick={() => onInactive(template)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Inactive
              </LoadingButton>
              <LoadingButton
                size="small"
                variant="outlined"
                color="error"
                loading={busy}
                startIcon={<CloseOutlinedIcon />}
                onClick={() => onReject(template)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Reject
              </LoadingButton>
            </>
          ) : null}
          {template.approval_status === "inactive" ? (
            <LoadingButton
              size="small"
              variant="contained"
              color="success"
              loading={busy}
              startIcon={<CheckOutlinedIcon />}
              onClick={() => onReactivate(template)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Reactivate
            </LoadingButton>
          ) : null}
        </Row>
      </Row>
    </Paper>
  )
}

export default function ConciergeInboxTemplateApprovalsSection({
  onStatusMessage,
}) {
  const axios = useAuthorizedAxiosClient()
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [actingId, setActingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState("approved")
  const { viewTemplate, viewDialogOpen, openViewDialog, closeViewDialog } =
    useConciergeInboxTemplateViewDialogState()

  const loadTemplates = useCallback(() => {
    setIsLoading(true)
    return axios
      .get(APPROVALS_URL, { params: { approval_status: statusFilter } })
      .then(({ data }) => {
        setTemplates(Array.isArray(data?.results) ? data.results : [])
      })
      .catch(({ response }) => {
        setTemplates([])
        onStatusMessage?.(
          parseApiErrorMessage(
            response?.data,
            "Could not load template approvals"
          ),
          "error"
        )
      })
      .finally(() => setIsLoading(false))
  }, [axios, onStatusMessage, statusFilter])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  async function handleAction(template, action) {
    const key = `${template.medium}-${template.id}`
    setActingId(key)
    try {
      const url = `${APPROVALS_URL}${template.medium}/${template.id}/${action}/`
      await axios.post(url)
      const messages = {
        reject: `${template.name} rejected.`,
        inactive: `${template.name} deactivated.`,
        reactivate: `${template.name} reactivated.`,
      }
      onStatusMessage?.(
        messages[action] || `${template.name} updated.`,
        action === "reactivate" ? "success" : "info"
      )
      loadTemplates()
    } catch ({ response }) {
      onStatusMessage?.(
        parseApiErrorMessage(
          response?.data,
          "Could not update template approval"
        ),
        "error"
      )
    } finally {
      setActingId(null)
    }
  }

  const inactiveCount = templates.filter(
    (template) => template.approval_status === "inactive"
  ).length

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Column sx={{ gap: 2 }}>
        <Column sx={{ gap: 0.75 }}>
          <Typography variant="h6" fontWeight={700}>
            Inbox template management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email and SMS reply templates your concierge creates in
            Messages. Concierges run AI review, verify the content, then save;
            you can reject or deactivate templates afterward. Approved templates
            are available in their inbox reply picker immediately.
          </Typography>
        </Column>

        <Row sx={{ gap: 1, flexWrap: "wrap" }}>
          {[
            { value: "approved", label: "Approved" },
            { value: "inactive", label: "Inactive" },
            { value: "rejected", label: "Rejected" },
          ].map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              color={statusFilter === option.value ? "secondary" : "default"}
              onClick={() => setStatusFilter(option.value)}
              sx={{ fontWeight: 600 }}
            />
          ))}
          {inactiveCount > 0 ? (
            <Chip
              size="small"
              color="default"
              label={`${inactiveCount} inactive`}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Row>

        <ConditionalPurpleLinearProgress visible={isLoading} />

        {!isLoading && templates.length === 0 ? (
          <Alert severity="info">{`No ${statusFilter} inbox templates.`}</Alert>
        ) : null}

        {!isLoading
          ? templates.map((template) => (
              <TemplateApprovalRow
                key={`${template.medium}-${template.id}`}
                template={template}
                actingId={actingId}
                onReject={(item) => handleAction(item, "reject")}
                onInactive={(item) => handleAction(item, "inactive")}
                onReactivate={(item) => handleAction(item, "reactivate")}
                onView={openViewDialog}
              />
            ))
          : null}
      </Column>
      <ConciergeInboxTemplateViewDialog
        open={viewDialogOpen}
        template={viewTemplate}
        onClose={closeViewDialog}
      />
    </Box>
  )
}
